import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  CAMERA_TRACK_SLOTS,
  type CameraTrackSlot,
} from "../../interface/webrtc_media_slots";
import type { CameraFpsMap } from "../../types/camera_fps";
import {
  createInitialMultiDisplayLayoutState,
  EXTERNAL_DISPLAY_ROLES,
  ExternalDisplayHostApi,
  ExternalDisplayRole,
  MediaSinkRegistration,
  MultiDisplayLayoutStateMap,
  type CameraFpsHandler,
  type ControlModeHandler,
  type ExternalDisplayWarningState,
  type WarningHandler,
  type WindowManagementPermissionState,
} from "./external_display_types";
import type { CameraMediaSinkRegistry } from "./media_sink_registry";
import {
  applyPermissionStateToLayout,
  computeRolePlacementsFromScreens,
  getAutoOpenRoles,
  getDisplayRoutePath,
  getFallbackPlacement,
  movePopupToBounds,
  openDisplayPopup,
  queryWindowManagementPermission,
  resolveWindowPlacements,
  type WindowPlacementBounds,
} from "./window_management";

type ExternalDisplayManager = {
  layoutState: MultiDisplayLayoutStateMap;
  hasTripleMonitor: boolean;
  hasExtendedDisplays: boolean;
  openableRoles: ExternalDisplayRole[];
  // 열어야 할 모든 보조 모니터 role 의 창이 열려 있고 전체화면인지(없으면 N/A→true).
  allDisplaysFullscreen: boolean;
  openDisplay: (role: ExternalDisplayRole) => Promise<void>;
  autoOpenAvailableDisplays: () => Promise<number>;
  openAvailableDisplaysSync: () => void;
  repositionDisplays: () => Promise<void>;
  // 권한/화면개수를 미리 확보(로그인 화면 마운트 시 호출) — 클릭 시점에 screenCount 를 알게 한다.
  ensureScreenPermission: () => Promise<number>;
  // 아직 못 연(팝업 차단/누락) 보조 디스플레이가 있는지 — 수동 "다시 펼치기" 버튼 노출 판단용.
  hasPendingDisplays: boolean;
  // 직접제어(direct control) 모드 활성 여부를 콕핏에서 매니저로 보고 → 보조 팝업에 broadcast.
  // (비활성 시 보조 화면은 영상 대신 안내 카드만 표시.)
  setDirectControlActive: (active: boolean) => void;
  // RTT 위험 등 경고 상태를 콕핏에서 매니저로 보고 → 보조 팝업이 동일한 붉은 경고 프레임 표시.
  setWarning: (warning: ExternalDisplayWarningState) => void;
};

const MONITOR_AVAILABILITY_REFRESH_MS = 1500;

function isExternalDisplayRole(role: string): role is ExternalDisplayRole {
  return EXTERNAL_DISPLAY_ROLES.includes(role as ExternalDisplayRole);
}

function useLatestValue<T>(value: T) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}

export function useExternalDisplayManager(
  baseUrl: string,
  mediaSinkRegistry: CameraMediaSinkRegistry,
  cameraReceiverFps?: CameraFpsMap,
): ExternalDisplayManager {
  const [layoutState, setLayoutState] = useState(
    createInitialMultiDisplayLayoutState,
  );
  const [hasTripleMonitor, setHasTripleMonitor] = useState(false);
  // 보조 모니터가 1개 이상(=화면 2개 이상)이면 외부 디스플레이를 사용할 수 있다.
  const [screenCount, setScreenCount] = useState(0);
  // 제스처 시점에 열려고 했으나 팝업 차단 등으로 아직 못 연 role 집합.
  // 핵심 견고성 장치: 브라우저가 "제스처 1회당 팝업 1개"만 허용해 두 번째(보통 right)가
  // 막힌 경우, 다음 사용자 제스처/포커스(=새 transient activation) 때 자동으로 재시도한다.
  // → "right 가 연결 후에야 뜬다 / left 만 뜬다" 증상의 자가 치유 경로.
  const [pendingDisplayRoles, setPendingDisplayRoles] = useState<
    ExternalDisplayRole[]
  >([]);
  const pendingDisplayRolesRef = useLatestValue(pendingDisplayRoles);
  const latestLayoutStateRef = useLatestValue(layoutState);
  const latestCameraReceiverFpsRef = useLatestValue(cameraReceiverFps);
  const cameraFpsSubscribersRef = useRef<
    Partial<Record<CameraTrackSlot, Set<CameraFpsHandler>>>
  >({});
  // 직접제어 모드 활성 여부 + 그 변경을 구독하는 보조 팝업 핸들러 집합.
  const directControlActiveRef = useRef(false);
  const controlModeSubscribersRef = useRef<Set<ControlModeHandler>>(new Set());
  const warningStateRef = useRef<ExternalDisplayWarningState>({
    active: false,
    rttMs: null,
  });
  const warningSubscribersRef = useRef<Set<WarningHandler>>(new Set());
  // 백그라운드 화면 폴링(refreshMonitorAvailability)이 getScreenDetails().screens 에서
  // 계산해 둔 role 별 "실제 모니터 좌표". 동기 팝업 열기(openRoleSyncImmediate)는 이 캐시를
  // 사용해 처음부터 올바른 모니터에 창을 만든다 — fallback(opener 화면 기준 추정) 후 moveTo
  // 로 옮기는 방식은 크로스-스크린 이동이 브라우저에 거부되어 둘 다 왼쪽에 남는 문제가 있었다.
  const cachedRolePlacementsRef = useRef<Record<
    ExternalDisplayRole,
    WindowPlacementBounds
  > | null>(null);
  // 가장 최근에 조회한 window-management 권한 상태. 미부여 시 좌/우 분리 불가 경고를 1회만 띄운다.
  const windowManagementPermissionRef =
    useRef<WindowManagementPermissionState>("unsupported");
  const permissionWarningShownRef = useRef(false);

  const readCameraSlotFps = useCallback(
    (slot: CameraTrackSlot): number | null => {
      const fps = latestCameraReceiverFpsRef.current?.[slot];
      return typeof fps === "number" && Number.isFinite(fps) ? fps : null;
    },
    [latestCameraReceiverFpsRef],
  );

  const patchRoleState = useCallback(
    (
      role: ExternalDisplayRole,
      updater: Partial<MultiDisplayLayoutStateMap[ExternalDisplayRole]>,
    ) => {
      setLayoutState((prev) => ({
        ...prev,
        [role]: {
          ...prev[role],
          ...updater,
        },
      }));
    },
    [],
  );

  const registerSink = useCallback(
    (registration: MediaSinkRegistration) => {
      const wrappedRegistration: MediaSinkRegistration = {
        ...registration,
        attach: (stream) => {
          registration.attach(stream);
          if (isExternalDisplayRole(registration.role)) {
            patchRoleState(registration.role, {
              blocked: false,
              connected: true,
              windowRef:
                registration.windowRef ||
                latestLayoutStateRef.current[registration.role].windowRef,
            });
          }
        },
        detach: () => {
          registration.detach();
          if (isExternalDisplayRole(registration.role)) {
            patchRoleState(registration.role, {
              connected: false,
            });
          }
        },
      };
      const unregister = mediaSinkRegistry.registerSink(wrappedRegistration);

      if (isExternalDisplayRole(registration.role)) {
        patchRoleState(registration.role, {
          blocked: false,
          windowRef:
            registration.windowRef ||
            latestLayoutStateRef.current[registration.role].windowRef,
        });
      }

      return () => {
        unregister();
        if (isExternalDisplayRole(registration.role)) {
          patchRoleState(registration.role, {
            connected: false,
          });
        }
      };
    },
    [latestLayoutStateRef, mediaSinkRegistry, patchRoleState],
  );

  const notifyWindowClosed = useCallback(
    (role: ExternalDisplayRole) => {
      patchRoleState(role, {
        connected: false,
        screenId: null,
        windowRef: null,
        fullscreen: false,
      });
    },
    [patchRoleState],
  );

  // 보조 모니터 팝업이 fullscreenchange 때 자신의 전체화면 상태를 보고한다.
  // 장치 준비 단계의 "좌/우 화면: 열림+전체화면" 게이트가 이 값을 읽는다.
  const reportFullscreen = useCallback(
    (role: ExternalDisplayRole, isFullscreen: boolean) => {
      if (!isExternalDisplayRole(role)) {
        return;
      }
      patchRoleState(role, { fullscreen: isFullscreen });
    },
    [patchRoleState],
  );

  const subscribeCameraFps = useCallback(
    (slot: CameraTrackSlot, handler: CameraFpsHandler) => {
      const subscribers =
        cameraFpsSubscribersRef.current[slot] || new Set<CameraFpsHandler>();
      subscribers.add(handler);
      cameraFpsSubscribersRef.current[slot] = subscribers;
      handler(readCameraSlotFps(slot));

      return () => {
        const currentSubscribers = cameraFpsSubscribersRef.current[slot];
        if (!currentSubscribers) {
          return;
        }

        currentSubscribers.delete(handler);
        if (currentSubscribers.size === 0) {
          delete cameraFpsSubscribersRef.current[slot];
        }
      };
    },
    [readCameraSlotFps],
  );

  // 보조 팝업이 직접제어 모드 활성 여부를 구독한다. 구독 즉시 현재 값을 1회 전달.
  const subscribeControlMode = useCallback((handler: ControlModeHandler) => {
    controlModeSubscribersRef.current.add(handler);
    handler(directControlActiveRef.current);
    return () => {
      controlModeSubscribersRef.current.delete(handler);
    };
  }, []);

  // 콕핏(직접제어 모드를 아는 곳)에서 호출 → 구독 중인 모든 보조 팝업에 broadcast.
  // 동일 값이면 무시(불필요한 재렌더 방지).
  const setDirectControlActive = useCallback((active: boolean) => {
    if (directControlActiveRef.current === active) {
      return;
    }
    directControlActiveRef.current = active;
    controlModeSubscribersRef.current.forEach((handler) => {
      handler(active);
    });
  }, []);

  const subscribeWarning = useCallback((handler: WarningHandler) => {
    warningSubscribersRef.current.add(handler);
    handler(warningStateRef.current);
    return () => {
      warningSubscribersRef.current.delete(handler);
    };
  }, []);

  // 콕핏(RTT 위험을 아는 곳)에서 호출 → 구독 중인 모든 보조 팝업에 경고 상태 broadcast.
  // 동일 값이면 무시(불필요한 재렌더 방지).
  const setWarning = useCallback((next: ExternalDisplayWarningState) => {
    const prev = warningStateRef.current;
    if (prev.active === next.active && prev.rttMs === next.rttMs) {
      return;
    }
    warningStateRef.current = next;
    warningSubscribersRef.current.forEach((handler) => {
      handler(next);
    });
  }, []);

  const closeAllDisplayWindows = useCallback(() => {
    EXTERNAL_DISPLAY_ROLES.forEach((role) => {
      const popupWindow = latestLayoutStateRef.current[role].windowRef;
      if (!popupWindow || popupWindow.closed) {
        return;
      }

      try {
        popupWindow.close();
      } catch (_err) {
        // Ignore close failures during teardown.
      }
    });
  }, [latestLayoutStateRef]);

  const syncClosedWindows = useCallback(() => {
    setLayoutState((prev) => {
      let changed = false;
      const nextState = { ...prev };

      EXTERNAL_DISPLAY_ROLES.forEach((role) => {
        const popupWindow = prev[role].windowRef;
        if (!popupWindow || !popupWindow.closed) {
          return;
        }

        changed = true;
        nextState[role] = {
          ...prev[role],
          connected: false,
          screenId: null,
          windowRef: null,
          fullscreen: false,
        };
      });

      return changed ? nextState : prev;
    });
  }, []);

  const ensureDisplayUrl = useCallback(
    (popupWindow: Window, role: ExternalDisplayRole) => {
      const nextPath = getDisplayRoutePath(baseUrl, role);

      try {
        if (popupWindow.location.pathname !== nextPath) {
          popupWindow.location.replace(nextPath);
        }
      } catch (_err) {
        // Ignore navigation failures for manually moved windows.
      }
    },
    [baseUrl],
  );

  // 현재 연결된 화면 개수를 조회해 상태를 갱신하고, 그 개수를 반환한다.
  // getScreenDetails 미지원/권한 거부 시에는 0을 반환해 외부 디스플레이를 비활성화한다.
  // 부수효과(중요): getScreenDetails().screens 에서 role 별 실제 모니터 좌표를 계산해
  // cachedRolePlacementsRef 에 저장한다 → 동기 팝업 열기가 처음부터 올바른 모니터에 창을 만든다.
  // 또한 window-management 권한 상태를 갱신해, 미부여 시 좌/우 분리 불가 경고를 1회 띄운다.
  const refreshMonitorAvailability = useCallback(async () => {
    if (typeof window.getScreenDetails !== "function") {
      setHasTripleMonitor(false);
      setScreenCount(0);
      cachedRolePlacementsRef.current = null;
      windowManagementPermissionRef.current = "unsupported";
      return 0;
    }

    // 화면 enumerate 전에 권한 상태를 먼저 조회한다(미부여면 getScreenDetails 가 throw 하거나
    // opener 화면만 반환할 수 있음). 미부여 시 명확한 조치 안내 로그를 1회만 남긴다.
    const permissionState = await queryWindowManagementPermission(window);
    windowManagementPermissionRef.current = permissionState;

    try {
      const screenDetails = await window.getScreenDetails();
      const screens = Array.isArray(screenDetails?.screens)
        ? screenDetails.screens
        : [];
      const nextCount = screens.length;
      setHasTripleMonitor(nextCount >= 3);
      setScreenCount(nextCount);
      cachedRolePlacementsRef.current =
        computeRolePlacementsFromScreens(screens);

      // 다중 모니터인데 권한이 부여되지 않아 좌/우를 분리 배치할 수 없는 경우 1회 경고.
      if (
        nextCount >= 2 &&
        permissionState !== "granted" &&
        !permissionWarningShownRef.current
      ) {
        permissionWarningShownRef.current = true;
        // eslint-disable-next-line no-console
        console.warn(
          "[external-display] window-management 권한 필요 — 좌/우 화면 분리 배치 불가; " +
            "키오스크 모드는 docs/KIOSK_MODE.md 참고",
        );
      }

      return nextCount;
    } catch (_err) {
      setHasTripleMonitor(false);
      setScreenCount(0);
      cachedRolePlacementsRef.current = null;

      // getScreenDetails 가 throw(주로 권한 미부여) → 좌/우 분리 불가 안내 1회.
      if (!permissionWarningShownRef.current) {
        permissionWarningShownRef.current = true;
        // eslint-disable-next-line no-console
        console.warn(
          "[external-display] window-management 권한 필요 — 좌/우 화면 분리 배치 불가; " +
            "키오스크 모드는 docs/KIOSK_MODE.md 참고",
        );
      }
      return 0;
    }
  }, []);

  const repositionDisplays = useCallback(async () => {
    const resolvedPlacements = await resolveWindowPlacements(window);

    setLayoutState((prev) =>
      applyPermissionStateToLayout(prev, resolvedPlacements.permissionState),
    );

    EXTERNAL_DISPLAY_ROLES.forEach((role) => {
      const popupWindow = latestLayoutStateRef.current[role].windowRef;
      if (!popupWindow || popupWindow.closed) {
        return;
      }

      const bounds = resolvedPlacements.placements[role];
      movePopupToBounds(popupWindow, bounds);
      patchRoleState(role, {
        blocked: false,
        screenId: bounds.screenId,
        windowRef: popupWindow,
        // window-management 권한이 부여되어 실제 모니터 좌표로 가득 채워 배치했으면
        // "주소칸만 있는 전체화면(immersive)" 으로 간주 → 준비 게이트 통과(팝업 self-report
        // 가 누락/지연돼도 진행). 권한 미부여면 건드리지 않는다(팝업 self-report 에 위임).
        ...(resolvedPlacements.permissionState === "granted"
          ? { fullscreen: true }
          : {}),
      });
    });
  }, [latestLayoutStateRef, patchRoleState]);

  // 실제 팝업 윈도우를 열고 위치를 잡는 핵심 로직. (게이트 검사는 호출 측 책임)
  // 이미 열려 있는(닫히지 않은) 윈도우가 있으면 재사용해 멱등성을 보장한다.
  const openDisplayWindow = useCallback(
    async (role: ExternalDisplayRole) => {
      const existingWindow = latestLayoutStateRef.current[role].windowRef;
      const fallbackBounds = getFallbackPlacement(window, role);
      // 캐시된 실제 모니터 좌표가 있으면 그것으로 연다(처음부터 올바른 모니터). 없으면 fallback.
      const openBounds =
        cachedRolePlacementsRef.current?.[role] ?? fallbackBounds;
      const displayUrl = getDisplayRoutePath(baseUrl, role);
      let popupWindow = existingWindow && !existingWindow.closed
        ? existingWindow
        : null;

      if (!popupWindow) {
        popupWindow = openDisplayPopup(window, displayUrl, role, openBounds);
      }

      if (!popupWindow) {
        patchRoleState(role, {
          blocked: true,
        });
        return;
      }

      ensureDisplayUrl(popupWindow, role);
      popupWindow.focus?.();

      patchRoleState(role, {
        blocked: false,
        windowRef: popupWindow,
      });

      const resolvedPlacements = await resolveWindowPlacements(window);

      setLayoutState((prev) =>
        applyPermissionStateToLayout(prev, resolvedPlacements.permissionState),
      );

      const bounds = resolvedPlacements.placements[role] || fallbackBounds;
      movePopupToBounds(popupWindow, bounds);
      patchRoleState(role, {
        blocked: false,
        screenId: bounds.screenId,
        windowRef: popupWindow,
        // 권한 부여 → 실제 모니터로 가득 채워 배치 → immersive 준비됨으로 표시.
        ...(resolvedPlacements.permissionState === "granted"
          ? { fullscreen: true }
          : {}),
      });
    },
    [
      baseUrl,
      ensureDisplayUrl,
      latestLayoutStateRef,
      patchRoleState,
    ],
  );

  // 단일 role 수동 열기. 외부 디스플레이 사용 가능(화면 2개 이상) 여부를 확인하고,
  // 해당 role이 현재 화면 구성에서 열 수 있는 대상일 때만 윈도우를 연다.
  const openDisplay = useCallback(
    async (role: ExternalDisplayRole) => {
      const currentScreenCount =
        screenCount >= 2 ? screenCount : await refreshMonitorAvailability();
      if (!getAutoOpenRoles(currentScreenCount).includes(role)) {
        return;
      }

      await openDisplayWindow(role);
    },
    [openDisplayWindow, refreshMonitorAvailability, screenCount],
  );

  // 콕핏 진입 시 호출 — 모니터 가용성을 갱신한 뒤, 현재 화면 개수에서 열 수 있는
  // 모든 외부 디스플레이를 자동으로 연다. 이미 열려 있는 role은 건너뛰어 멱등하며,
  // getScreenDetails 미지원/권한 거부 시에는 아무 동작도 하지 않는다(단일 모니터 안전).
  // 호출 측(콕핏 진입 effect)이 다중 모니터 여부로 메인 윈도우 전체화면을 결정할 수 있도록
  // 갱신된 화면 개수를 반환한다.
  // 자동 열기/닫기가 관리 중인 role 집합 — 사용자가 수동으로 연 윈도우를
  // 화면 개수 변동(unplug) 때 무단으로 닫지 않도록 추적한다.
  const autoManagedRolesRef = useRef<Set<ExternalDisplayRole>>(new Set());

  // 한 role 을 "아직 못 연 대기열"에 등록/해제한다(상태 + ref 동기 갱신).
  // (autoOpenAvailableDisplays 보다 먼저 선언해야 한다 — useCallback 의존성 배열이 렌더 시점에
  //  이 참조를 읽으므로 TDZ 를 피하려면 위에 와야 한다.)
  const markRolePending = useCallback((role: ExternalDisplayRole) => {
    setPendingDisplayRoles((prev) =>
      prev.includes(role) ? prev : [...prev, role],
    );
  }, []);
  const clearRolePending = useCallback((role: ExternalDisplayRole) => {
    setPendingDisplayRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : prev,
    );
  }, []);

  // 콕핏 진입 후 보강(gap-filler) 경로. 주의: 이 함수는 getScreenDetails 를 await 한 뒤
  // window.open 을 호출하므로 transient activation 을 잃어 팝업이 차단될 수 있다.
  // → 더 이상 "주 경로"가 아니다(주 경로는 제스처 시점의 openAvailableDisplaysSync).
  //   여기서는 이미 열린 role 은 건너뛰어 멱등하게 동작하고, 열지 못한 role 은
  //   pending 으로 남겨 다음 포커스 제스처에서 자동 재시도되게 한다.
  const autoOpenAvailableDisplays = useCallback(async (): Promise<number> => {
    const currentScreenCount = await refreshMonitorAvailability();
    const roles = getAutoOpenRoles(currentScreenCount);

    for (const role of roles) {
      autoManagedRolesRef.current.add(role);
      const existingWindow = latestLayoutStateRef.current[role].windowRef;
      if (existingWindow && !existingWindow.closed) {
        clearRolePending(role);
        continue;
      }

      await openDisplayWindow(role);

      // openDisplayWindow 가 (await 이후 activation 소진으로) 창을 못 열었으면 pending 등록.
      const opened = latestLayoutStateRef.current[role].windowRef;
      if (!opened || opened.closed) {
        markRolePending(role);
      } else {
        clearRolePending(role);
      }
    }

    return currentScreenCount;
  }, [
    clearRolePending,
    latestLayoutStateRef,
    markRolePending,
    openDisplayWindow,
    refreshMonitorAvailability,
  ]);

  // 사용자 제스처 컨텍스트에서 "지금 당장" 동기로 한 role 의 팝업을 연다.
  //
  // 타이밍 제약(중요):
  //  - window.open 은 반드시 어떤 await 보다도 먼저(=transient activation 이 살아있는 동안)
  //    호출해야 한다. await 이후에는 활성화가 소진되어 팝업이 차단된다.
  //  - 브라우저/팝업 차단기는 "제스처 1회당 새 창 1개"만 허용하는 경우가 많다.
  //    따라서 한 번의 클릭으로 left+right 두 개를 모두 여는 것은 신뢰할 수 없다.
  //    두 번째가 막히면 그 role 을 pending 으로 남겨 다음 제스처/포커스에서 재시도한다.
  //
  // 반환값: 이번 호출에서 (재사용 포함) 창이 확보됐으면 true.
  const openRoleSyncImmediate = useCallback(
    (role: ExternalDisplayRole): boolean => {
      autoManagedRolesRef.current.add(role);
      const existing = latestLayoutStateRef.current[role].windowRef;
      if (existing && !existing.closed) {
        clearRolePending(role);
        return true;
      }
      // 핵심: 백그라운드 폴링이 캐시해 둔 실제 모니터 좌표가 있으면 그것으로 창을 만든다.
      // 그래야 처음부터 올바른 모니터에 뜬다(크로스-스크린 moveTo 의존 제거). 캐시가 없으면
      // (권한 전/미지원) opener 화면 기준 추정 fallback 으로 연 뒤 reconcile 이 보정한다.
      const openBounds =
        cachedRolePlacementsRef.current?.[role] ??
        getFallbackPlacement(window, role);
      const popup = openDisplayPopup(
        window,
        getDisplayRoutePath(baseUrl, role),
        role,
        openBounds,
      );
      if (popup) {
        patchRoleState(role, { blocked: false, windowRef: popup });
        clearRolePending(role);
        return true;
      }
      // 팝업 차단됨 — pending 으로 남겨 다음 제스처/포커스에서 재시도.
      patchRoleState(role, { blocked: true });
      markRolePending(role);
      return false;
    },
    [baseUrl, clearRolePending, latestLayoutStateRef, markRolePending, patchRoleState],
  );

  // 열린 창들의 실제 화면 배치 보정 + 현재 화면구성에서 열면 안 되는 창 정리(닫기).
  // (동기 open 직후 비동기로 1회 실행. 멱등.)
  const reconcileOpenedDisplays = useCallback(async () => {
    const count = await refreshMonitorAvailability();
    const desired = new Set(getAutoOpenRoles(count));
    const resolved = await resolveWindowPlacements(window);
    setLayoutState((prev) =>
      applyPermissionStateToLayout(prev, resolved.permissionState),
    );
    EXTERNAL_DISPLAY_ROLES.forEach((role) => {
      const popup = latestLayoutStateRef.current[role].windowRef;
      if (!popup || popup.closed) {
        return;
      }
      if (!desired.has(role)) {
        try {
          popup.close();
        } catch (_err) {
          // 닫기 실패 무시.
        }
        autoManagedRolesRef.current.delete(role);
        clearRolePending(role);
        notifyWindowClosed(role);
        return;
      }
      const bounds =
        resolved.placements[role] || getFallbackPlacement(window, role);
      movePopupToBounds(popup, bounds);
      patchRoleState(role, {
        blocked: false,
        screenId: bounds.screenId,
        windowRef: popup,
        // 권한 부여 → 실제 모니터로 가득 채워 배치 → immersive 준비됨으로 표시.
        ...(resolved.permissionState === "granted" ? { fullscreen: true } : {}),
      });
    });
  }, [
    clearRolePending,
    latestLayoutStateRef,
    notifyWindowClosed,
    patchRoleState,
    refreshMonitorAvailability,
  ]);

  // 사용자 제스처(로그인 클릭, 수동 "디스플레이 펼치기" 버튼)에서 호출.
  // 화면 개수는 백그라운드 폴링/ensureScreenPermission 으로 미리 파악된 screenCount 를 쓰고,
  // 아직 0(권한 전/미조회)이면 멀티모니터를 가정해 양쪽을 낙관적으로 시도한 뒤
  // reconcileOpenedDisplays 가 실제 구성에 맞춰 위치 보정/정리한다.
  //
  // 두 개를 한 제스처에서 모두 열어보되, 막힌 role 은 pending 에 남겨
  // 다음 제스처/포커스에서 자동 재시도되도록 한다(완전한 1-제스처 보장은 불가능 — 브라우저 제약).
  const openAvailableDisplaysSync = useCallback(() => {
    const supportsScreens = typeof window.getScreenDetails === "function";
    const rolesToOpen: ExternalDisplayRole[] =
      screenCount >= 2
        ? getAutoOpenRoles(screenCount)
        : screenCount === 1
          ? []
          : supportsScreens
            ? [...EXTERNAL_DISPLAY_ROLES]
            : [];
    if (rolesToOpen.length === 0) {
      return;
    }

    rolesToOpen.forEach((role) => {
      openRoleSyncImmediate(role);
    });

    void reconcileOpenedDisplays();
  }, [openRoleSyncImmediate, reconcileOpenedDisplays, screenCount]);

  // 로그인 화면 마운트 시 등 "클릭 전에" 호출 — window-management 권한/화면 개수를 미리 확보한다.
  // 이렇게 해야 로그인 클릭 시점에 screenCount 가 정확해 getAutoOpenRoles 가 올바른 role 을 연다
  // (첫 로그인에 권한 미부여로 screenCount=0 이던 문제 완화). 미지원/거부는 0 을 반환(안전).
  // 참고: 일부 브라우저는 getScreenDetails 첫 호출에서 권한 프롬프트를 띄울 수 있으나,
  //       대개는 사용자 제스처 없이도 'granted'/'prompt' 상태 조회 및 enumerate 가 가능하다.
  const ensureScreenPermission = useCallback(async (): Promise<number> => {
    return refreshMonitorAvailability();
  }, [refreshMonitorAvailability]);

  // pending(차단되어 못 연) role 을 새 transient activation(포커스 복귀) 때 자동 재시도한다.
  // 사용자가 메인 창으로 포커스를 옮길 때마다(예: 차량 선택 클릭 직전/직후) 한 번 더 열어본다.
  // window.open 은 포커스 핸들러(=사용자 제스처 파생) 안에서 동기로 호출되므로 활성화가 살아있다.
  const retryPendingDisplays = useCallback(() => {
    const pending = pendingDisplayRolesRef.current;
    if (pending.length === 0) {
      return;
    }
    let openedAny = false;
    pending.forEach((role) => {
      if (openRoleSyncImmediate(role)) {
        openedAny = true;
      }
    });
    if (openedAny) {
      void reconcileOpenedDisplays();
    }
  }, [openRoleSyncImmediate, pendingDisplayRolesRef, reconcileOpenedDisplays]);

  useEffect(() => {
    window.addEventListener("focus", retryPendingDisplays);
    return () => {
      window.removeEventListener("focus", retryPendingDisplays);
    };
  }, [retryPendingDisplays]);

  // 콕핏에 머무는 동안의 모니터 핫플러그 대응(P1-6/P1-7).
  // screenCount 가 변하면: ① 새로 열 수 있게 된 role 은 자동으로 열고(멱등),
  // ② 더 이상 현재 화면 구성에서 열 수 없는(getAutoOpenRoles 에서 빠진) role 중
  //    자동 열기가 관리하던 윈도우는 닫는다. 단일 모니터(0~1)에서는 아무 것도 열지 않는다.
  const prevScreenCountRef = useRef(screenCount);
  useEffect(() => {
    const prevCount = prevScreenCountRef.current;
    prevScreenCountRef.current = screenCount;
    if (screenCount === prevCount) {
      return;
    }

    // 자동 열기가 한 번도 동작하지 않았다면(콕핏 미진입 등) 핫플러그 자동화도 건너뛴다.
    if (autoManagedRolesRef.current.size === 0) {
      return;
    }

    const desiredRoles = new Set(getAutoOpenRoles(screenCount));

    // ② 빠진 role 닫기 — 자동 관리 대상이고 실제 윈도우가 있을 때만.
    EXTERNAL_DISPLAY_ROLES.forEach((role) => {
      if (desiredRoles.has(role) || !autoManagedRolesRef.current.has(role)) {
        return;
      }

      const popupWindow = latestLayoutStateRef.current[role].windowRef;
      if (popupWindow && !popupWindow.closed) {
        try {
          popupWindow.close();
        } catch (_err) {
          // 닫기 실패는 무시(이미 닫혔거나 권한 문제).
        }
      }
      autoManagedRolesRef.current.delete(role);
      notifyWindowClosed(role);
    });

    // ① 새로 가능해진 role 자동 열기.
    desiredRoles.forEach((role) => {
      autoManagedRolesRef.current.add(role);
      const existingWindow = latestLayoutStateRef.current[role].windowRef;
      if (existingWindow && !existingWindow.closed) {
        return;
      }
      void openDisplayWindow(role);
    });
  }, [
    latestLayoutStateRef,
    notifyWindowClosed,
    openDisplayWindow,
    screenCount,
  ]);

  // 보조 팝업이 읽는 host API(window.__ACEWORKS_EXTERNAL_DISPLAY_HOST__)는 컴포넌트 생애 동안
  // "동일한 객체 참조"여야 한다(#3). 이전에는 effect 본문에서 매 렌더마다 새 객체 리터럴을
  // 만들어, registerSink 의 attach 가 patchRoleState({connected:true}) 로 재렌더를 유발할 때마다
  // hostApi 참조가 바뀌었다. 그러면 팝업의 `currentHostApiRef.current === nextHostApi` 가드가
  // 깨져 cleanupRegistration → srcObject=null → 즉시 재attach → video.play() 가 AbortError 로
  // 조용히 거부되어(특히 늦게 열리는 우측 팝업) 미러가 검게 남았다.
  // → 콜백은 ref 로 매 렌더 갱신하되 host API 객체 자체는 1회만 생성해 참조를 고정한다.
  const hostCallbacksRef = useRef({
    registerSink,
    subscribeCameraFps,
    notifyWindowClosed,
    reportFullscreen,
    subscribeControlMode,
    subscribeWarning,
  });
  useLayoutEffect(() => {
    hostCallbacksRef.current = {
      registerSink,
      subscribeCameraFps,
      notifyWindowClosed,
      reportFullscreen,
      subscribeControlMode,
      subscribeWarning,
    };
  });
  const stableHostApiRef = useRef<ExternalDisplayHostApi | null>(null);
  if (stableHostApiRef.current === null) {
    stableHostApiRef.current = {
      registerSink: (registration) =>
        hostCallbacksRef.current.registerSink(registration),
      subscribeCameraFps: (slot, handler) =>
        hostCallbacksRef.current.subscribeCameraFps(slot, handler),
      notifyWindowClosed: (role) =>
        hostCallbacksRef.current.notifyWindowClosed(role),
      reportFullscreen: (role, isFullscreen) =>
        hostCallbacksRef.current.reportFullscreen(role, isFullscreen),
      subscribeControlMode: (handler) =>
        hostCallbacksRef.current.subscribeControlMode(handler),
      subscribeWarning: (handler) =>
        hostCallbacksRef.current.subscribeWarning(handler),
    };
  }
  const stableHostApi = stableHostApiRef.current;

  useEffect(() => {
    window.__ACEWORKS_EXTERNAL_DISPLAY_HOST__ = stableHostApi;
    window.addEventListener("focus", syncClosedWindows);
    void refreshMonitorAvailability();

    return () => {
      window.removeEventListener("focus", syncClosedWindows);
      if (window.__ACEWORKS_EXTERNAL_DISPLAY_HOST__ === stableHostApi) {
        delete window.__ACEWORKS_EXTERNAL_DISPLAY_HOST__;
      }
      closeAllDisplayWindows();
    };
  }, [
    closeAllDisplayWindows,
    refreshMonitorAvailability,
    stableHostApi,
    syncClosedWindows,
  ]);

  useEffect(() => {
    CAMERA_TRACK_SLOTS.forEach((slot) => {
      const subscribers = cameraFpsSubscribersRef.current[slot];
      if (!subscribers) {
        return;
      }

      const nextFps = readCameraSlotFps(slot);
      subscribers.forEach((handler) => {
        handler(nextFps);
      });
    });
  }, [cameraReceiverFps, readCameraSlotFps]);

  useEffect(() => {
    const handleRefresh = () => {
      void refreshMonitorAvailability();
    };
    const intervalId = window.setInterval(
      handleRefresh,
      MONITOR_AVAILABILITY_REFRESH_MS,
    );

    window.addEventListener("focus", handleRefresh);
    window.addEventListener("resize", handleRefresh);
    document.addEventListener("visibilitychange", handleRefresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleRefresh);
      window.removeEventListener("resize", handleRefresh);
      document.removeEventListener("visibilitychange", handleRefresh);
    };
  }, [refreshMonitorAvailability]);

  // 화면 2개 이상이면 외부 디스플레이를 사용할 수 있다(기존 hasTripleMonitor는 호환용 유지).
  const hasExtendedDisplays = screenCount >= 2;
  const openableRoles = getAutoOpenRoles(screenCount);
  // 열려 있어야 하는데(현재 화면구성 기준) 아직 못 연 보조 디스플레이가 있는가.
  // 수동 "디스플레이 다시 펼치기" 버튼 노출 판단에 사용.
  const hasPendingDisplays = pendingDisplayRoles.some((role) =>
    openableRoles.includes(role),
  );

  // 열어야 할 모든 보조 모니터 role 이 (열림 + 전체화면)인지. 열 게 없으면(단일/미지원)
  // 펼칠 대상이 없으므로 true(N/A=준비됨). 장치 준비 단계의 디스플레이 게이트에 사용.
  const allDisplaysFullscreen = openableRoles.every((role) => {
    const state = layoutState[role];
    const win = state?.windowRef;
    return Boolean(win) && !win!.closed && Boolean(state?.fullscreen);
  });

  return {
    layoutState,
    hasTripleMonitor,
    hasExtendedDisplays,
    openableRoles,
    allDisplaysFullscreen,
    openDisplay,
    autoOpenAvailableDisplays,
    openAvailableDisplaysSync,
    repositionDisplays,
    ensureScreenPermission,
    hasPendingDisplays,
    setDirectControlActive,
    setWarning,
  };
}
